import BigNumber from 'bignumber.js';
import {MAX_DECIMALS_IN_CRYPTO} from '~/config';
import {invariant} from '~/library/toolbox';
import {AccountId, Resource} from '~/library/types';
import {selectAssetsResource} from '~/redux/assets/assetsResource';
import {selectFinancesResource} from '~/redux/finances';
import {OperationAlias} from '~/redux/finances/domain';
import {getAllOperations} from '~/redux/finances/financesPrimitives';
import {createOperationRoute} from '~/redux/finances/financesRouting';
import {BlockchainAccount, FiatAccount, selectAccountsFromAssets} from './Accounts';

export interface SellTxLimits {
    /**
     * Either balance or maximum limit on transaction
     */
    upper: BigNumber;
    /**
     * Minimal allowed amount to send
     */
    lower: BigNumber;
    /**
     * Upper limit on how much fee user can pay
     */
    fee: BigNumber;
}

export type SellTxResource = {
    accountFrom: BlockchainAccount;
    accountTo: FiatAccount;
    pathToBuy: string | null;
    limits: SellTxLimits;
};

export const selectSellTxResource = (from: AccountId, to: AccountId) => (state: AppModel) =>
    Resource.all(selectAssetsResource(state), selectFinancesResource(state).mapSuccess(getAllOperations)).mapSuccess(
        ([assets, operations]) => {
            const {fiatAccounts, blockchainAccounts} = selectAccountsFromAssets(assets);

            const accountFrom = blockchainAccounts.find(({id}) => id === from);
            const accountTo = fiatAccounts.find(({id}) => id === to);

            if (!accountFrom || !accountTo) {
                return null;
            }

            const minimum = assets.minimum.default[accountFrom.code];

            invariant(!!minimum, `Minimum amount for ${accountTo.code} not found`);

            const feeAssetBuyOperation =
                operations.find(o => OperationAlias.from(o) === 'buy' && o.target.id === accountFrom.blockchain.id) ??
                null;

            // truncating balance because Use All cannot work correctly if precisions are different
            const lowerLimit = BigNumber(minimum).decimalPlaces(accountFrom.decimals, BigNumber.ROUND_DOWN);

            const decimals = Math.min(accountFrom.decimals, MAX_DECIMALS_IN_CRYPTO);

            // truncating balance because Use All cannot work correctly if precisions are different
            const upperLimit = new BigNumber(accountFrom.balance).decimalPlaces(decimals, BigNumber.ROUND_DOWN);

            return {
                accountFrom,
                accountTo,
                limits: {
                    lower: lowerLimit,
                    upper: upperLimit,
                    fee: BigNumber(accountFrom.blockchain.balance),
                },
                pathToBuy: feeAssetBuyOperation && createOperationRoute(feeAssetBuyOperation),
            } satisfies SellTxResource;
        }
    );
